<template>
  <ListPage
    variant="page"
    :loading="mainLoading && !isBackgroundActiveContainersRefreshing"
    loadingText="Loading…"
    v-model:pageSize="pageSize"
    v-slot="{ pageSizeOptions, setPageSize }"
  >
    <div v-if="user.onboardedTerminalLogins">
      
      <TrackingHeader
        class="tw-mb-4"
        :isShowingAppointments="isShowingAppointments"
        @refresh="refreshContainers"
        @toggleAppointments="toggleAppointmentsView"
      >
        <template #search>
          <SearchInput
            v-model="searchInput"
            @search="searchTermChanged"
            placeholder="Search container, BL, customer…"
            class="tw-w-full"
          />
        </template>

        <template #actions>
          <EnterContainer
            @searchTermChanged="searchTermChanged"
            @containerListChanged="refreshContainers"
            :hideSearch="true"
          />
        </template>
      </TrackingHeader>

      <Modal
        v-model:isOpen="isContainerModalOpen"
        modalId="tracking-container-modal"
        maxWidth="7xl"
        ariaLabel="Container details"
      >
        <template #title>
          <div class="tw-text-base sm:tw-text-lg tw-font-semibold tw-text-slate-900 tw-mr-4 tw-inline-block">
            <CopyButton
              v-if="currentContainerFromList"
              :value="String(currentContainerFromList.containerId)"
              mode="wrap-content"
              title="Copy container ID"
              aria-label="Copy container ID to clipboard"
            >
              {{ currentContainerFromList.containerId }}
            </CopyButton>
            <span v-else>Container</span>
            <span
              v-if="currentContainerFromList?.isCancelled"
              class="tw-ml-2 tw-inline-flex tw-items-center tw-rounded-md tw-bg-rose-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-rose-800 tw-ring-1 tw-ring-inset tw-ring-rose-600/20"
              :title="currentContainerFromList.cancelledReason || 'Cancelled on order'"
            >
              Cancelled
            </span>
          </div>
          <div class="tw-text-sm tw-text-slate-600  tw-inline-block">
            <span v-if="currentContainerFromList?.billOfLading">
              BL#
              <CopyButton
                :value="currentContainerFromList.billOfLading.billOfLadingNumber"
                mode="wrap-content"
                title="Copy B/L number"
                aria-label="Copy B/L number to clipboard"
              >
                {{ currentContainerFromList.billOfLading.billOfLadingNumber }}
              </CopyButton>
            </span>
            <span v-if="currentContainerFromList?.bookingNumber">
              Booking #
              <CopyButton
                :value="currentContainerFromList.bookingNumber"
                mode="wrap-content"
                title="Copy Booking number"
                aria-label="Copy Booking number to clipboard"
              >
                {{ currentContainerFromList.bookingNumber }}
              </CopyButton>
            </span>
          </div>
        </template>
        <template #content>
          <div class="tw--m-5">
            <ContainerModal
              :containerId="currentContainerId"
              :containerFromList="currentContainerFromList"
              @close="closeContainerModal"
              @updateContainer="onContainerUpdated"
              :enableVessel="true"
              :vessels="vessels"
              @archiveContainer="archiveContainer"
              @showOrder="showOrder"
              @showNewOrder="showNewOrder"
            ></ContainerModal>
          </div>
        </template>
      </Modal>

      <AddNotesModal
        :open="containerNotesModalOpen"
        title="Container notes"
        :subtitle="containerForNotesModal ? String(containerForNotesModal.containerId) : undefined"
        :container="containerForNotesModal"
        @close="closeContainerNotes"
        @addedNote="onContainerNotesAdded"
        @archivedNotesUpdated="onContainerArchivedNotesUpdated"
      />

      <div class="tw-relative tw-z-30 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-p-3 tw-mb-4">
        <div class="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          
          <div class="tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-flex tw-flex-nowrap tw-items-center tw-gap-2">
            <div v-if="!isShowingAppointments" class="tw-flex tw-flex-nowrap tw-gap-2 tw-pb-1 sm:tw-pb-0">
              <button
                v-for="status in ['All', 'Unknown', 'NotFound', 'Onboard', 'NotAvailable', 'Available', 'OffDock'].filter(s => s !== 'Unknown' || stats.unknown > 0)"
                :key="status"
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-font-medium tw-transition tw-whitespace-nowrap  tw-text-[17px]"
                :class="'tw-border-slate-200 tw-text-slate-700 '
                    +
                      (currentStatus === status
                      ? 'tw-text-white ' + (({'Unknown': 'tw-bg-amber-700 ', 'NotFound': 'tw-bg-orange-700', 'Onboard': 'tw-bg-blue-700 ', 'NotAvailable': 'tw-bg-red-700', 'Available': 'tw-bg-emerald-700', 'OffDock': 'tw-bg-slate-700'})[status] || 'tw-bg-slate-700')
                      : (({'Unknown': 'tw-bg-amber-100 hover:tw-bg-amber-300 ', 'NotFound': 'tw-bg-orange-100 hover:tw-bg-orange-300', 'Onboard': 'tw-bg-blue-100 hover:tw-bg-blue-300', 'NotAvailable': 'tw-bg-red-200 hover:tw-bg-red-300', 'Available': 'tw-bg-emerald-100 hover:tw-bg-emerald-300', 'OffDock': 'tw-bg-slate-100 hover:tw-bg-slate-300'})[status] || ''))"
                @click="changeStatus(status)"
              >
                <span>{{ status === 'NotAvailable' ? 'Not Available' : status === 'NotFound' ? 'Not Found' : status === 'OffDock' ? 'Off Dock' : status }}</span>
                <span class="tw-inline-flex tw-min-w-[15px] tw-justify-center tw-px-1.5 tw-py-0.5 tw-text-[15px]"
                >
                  {{ status === 'All' ? stats.all : 
                     status === 'Onboard' ? stats.onboard : 
                     status === 'Available' ? stats.available : 
                     status === 'NotAvailable' ? stats.notAvailable : 
                     status === 'OffDock' ? stats.offDock : 
                     status === 'NotFound' ? stats.notFound : stats.unknown }}
                </span>
              </button>

              <label 
                v-if="containerNumbersChangedSincePageLoad.length + containerNumbersAddedSincePageLoad.length > 0"
                class="
                  tw-inline-flex tw-items-center tw-gap-2 
                  tw-rounded-full tw-border 
                  tw-mx-4 tw-px-2 tw-py-1.5 
                  tw-font-medium tw-text-[14px]
                  tw-transition tw-whitespace-nowrap  
                  tw-cursor-pointer
                  "
                :class="isShowChangedAndNewContainersOnly ? 'tw-bg-red-700 tw-text-white' : 'tw-bg-red-400'"
              >
                <input type="checkbox" v-model="isShowChangedAndNewContainersOnly"  /> 
                NEW
                
                <span
                class="tw-inline-flex tw-h-5 tw-min-w-[1.25rem] tw-line-height-1tw-items-center tw-justify-center tw-rounded-full tw-bg-slate-600 tw-px-1 tw-font-bold tw-text-white"
                >
                  {{ containerNumbersChangedSincePageLoad.length + containerNumbersAddedSincePageLoad.length }}
                </span>
              </label>
            </div>

            <div v-else-if="isShowingAppointments" class="tw-flex tw-flex-nowrap tw-gap-2">
              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition"
                :class="currentStatus === TERMINAL_APPOINTMENTS_STRING ? 'tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white/60'"
                @click="changeStatus(TERMINAL_APPOINTMENTS_STRING)"
              >
                <HourGlass class="tw-w-3 tw-h-3" />
                <span>Terminal appts</span>
                <span class="tw-px-1.5 tw-bg-white/20 tw-rounded-full">{{ stats.terminalAppointments || 0 }}</span>
              </button>

              <button
                type="button"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition"
                :class="currentStatus === DELIVERY_APPOINTMENTS_STRING ? 'tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white/60'"
                @click="changeStatus(DELIVERY_APPOINTMENTS_STRING)"
              >
                <EventSchedule class="tw-w-3 tw-h-3" />
                <span>Delivery appts</span>
                <span class="tw-px-1.5 tw-bg-white/20 tw-rounded-full">{{ stats.deliveryAppointments || 0 }}</span>
              </button>
            </div>

            <button
              v-if="isListFiltered"
              type="button"
              class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-rose-400 tw-text-white tw-px-3 tw-py-1.5 tw-font-medium tw-text-slate-700 tw-transition tw-whitespace-nowrap tw-text-[17px] hover:tw-bg-rose-700"
              @click="resetListFilters"
              v-tippy="{content: 'Reset active search/category filters', placement: 'top'}"
            >
              <font-awesome-icon icon="filter-circle-xmark"/>
            </button>
          </div>
          <div class="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
          
            
            <span v-if="isExportSelectionMode" class="tw-flex tw-items-center tw-gap-2">
              <button
                v-tippy="{content: 'Preview/Copy', placement: 'bottom'}"
                class="tw-relative tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
                :class="[exportSelectionFormat === 'csv' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                @click="exportSelectionFormat = 'csv'; isExportSelectionModalOpen = true;"
              >
                CSV
              </button>
              <button
                v-tippy="{content: 'Preview/Copy', placement: 'bottom'}"
                class="tw-relative tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
                :class="[exportSelectionFormat === 'text' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                @click="exportSelectionFormat = 'text'; isExportSelectionModalOpen = true;"
              >
                Text
              </button>
              
              <button
                class="tw-relative tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
                :class="[exportSelectionFormat === 'html' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                v-tippy="{content: 'Export/Download', placement: 'bottom'}"
                @click="exportSelectionFormat = 'html'; isExportSelectionModalOpen = true;"
              >
                HTML
              </button>

              &nbsp;

              <button
                class="tw-px-2 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
                :class="[exportSelectionColumnMode === 'single' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                v-tippy="{content: 'Single Column Mode', placement: 'bottom'}"
                @click="exportSelectionColumnMode = 'single'"
              >
                <font-awesome-icon
                  icon="bars"
                  class="tw-h-3.5 tw-w-3.5 tw-z-10 tw-text-slate-800"
                  :class="[exportSelectionColumnMode === 'single' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                />
                <span class="tw-pl-0.5">1</span>
              </button>

              <button
                class="tw-px-2 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
                :class="[exportSelectionColumnMode === 'multiple' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                v-tippy="{content: 'Multiple Column Mode', placement: 'bottom'}"
                @click="exportSelectionColumnMode = 'multiple'"
              >
                <font-awesome-icon
                  icon="bars"
                  class="tw-h-3.5 tw-w-3.5 tw-z-10 tw-text-slate-800"
                  :class="[exportSelectionColumnMode === 'multiple' ? 'tw-bg-slate-600 tw-text-white' : 'tw-border-slate-200']"
                />
                <span class="tw-pl-0.5">&infin;</span>
              </button>
            </span>
            <button
              class="tw-relative tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
              @click="isExportSelectionMode = !isExportSelectionMode; if (!isExportSelectionMode) { isExportSelectionModalOpen = false; }"
              :class="[isExportSelectionMode ? 'tw-bg-black tw-text-white' : '']"
            >
              <font-awesome-icon
                icon="download"
                class="tw-h-3.5 tw-w-3.5 tw-text-slate-800"
                :class="[isExportSelectionMode ? 'tw-bg-black tw-text-white' : '']"
              />
              Export
              <font-awesome-icon
                icon="circle-xmark"
                class="tw-h-2.5 tw-w-2.5 tw-z-100 tw-text-slate-800 tw-absolute tw-top-[-5px] tw-right-[-5px]"
                v-if="isExportSelectionMode"
              />
            </button>

            <span class="tw-text-slate-300">|</span>
            <AdvancedSort
              :availableFields="containerSortFields"
              v-model="multiSort"
              :isActive="isAdvancedSort"
              @update:modelValue="handleAdvancedSort"
              @activate="reactivateAdvancedSort"
            />
          </div>
          
          <div class="tw-flex tw-items-center tw-gap-3 tw-text-xs tw-text-slate-600 tw-flex-shrink-0">
            <template v-if="!isActiveContainersLoading">
              <span v-if="totalPages > 1">Page {{ pageNum + 1 }} of {{ totalPages }}</span>
              <span class="tw-text-slate-300">|</span>
              <label class="tw-flex tw-items-center tw-gap-2">
                <span>Rows</span>
                <select
                  class="tw-rounded-md tw-border-slate-200 tw-bg-white tw-px-1.5 tw-py-0.5 tw-outline-none"
                  :value="String(pageSize)"
                  @change="(e) => setPageSize(Number((e.target as HTMLSelectElement).value))"
                >
                  <option v-for="opt in pageSizeOptions" :key="opt" :value="String(opt)">{{ opt }}</option>
                </select>
              </label>
            </template>
          </div>
        </div>

        <div v-if="searchTerm" class="tw-mt-2 tw-pt-2 tw-border-t tw-border-slate-100 tw-text-[11px] tw-text-slate-500">
          Searching for: <span class="tw-font-bold tw-text-slate-800">{{ searchTerm }}</span>
        </div>
      </div>

      <div class="tw-mt-4">
        <ListPage
          ref='listPageRef'
          variant="card"
          v-slot="{ selection }"
          :selection-items="containers"
          :selection-key="'id'"
          :enable-selection="true"
          :loading="isActiveContainersLoading"
          loadingText="Loading containers…"
          :cardPadding="false"
        >
          <div
            v-if="!isActiveContainersLoading && (containers || []).length === 0"
            class="tw-p-10 tw-text-center"
          >
            <div class="tw-text-lg tw-font-semibold tw-text-slate-900 tw-mb-1">No containers found</div>
            <div class="tw-text-sm tw-text-slate-600 tw-mb-4">Try adjusting filters or search.</div>
            <Button @click="changeStatus('All')">Show all</Button>
          </div>

          <ContainerTable
            v-else
            @refreshContainers="refreshContainers"
            @archiveContainer="archiveContainer"
            :containers="currentTabContainers"
            :isHideCustomerInfo="!user.enableCustomerInfo"
            :containerNumbersAddedSincePageLoad="containerNumbersAddedSincePageLoad"
            :containerNumbersChangedSincePageLoad="containerNumbersChangedSincePageLoad"
            :lastOpenedContainerId="lastOpenedContainerId"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
            @updateSortBy="updateSortBy"
            @selectContainer="selectContainer"
            @openContainerNotes="openContainerNotes"
            :typeOfAppointmentsShowing="!isShowingAppointments ? '' : (currentStatus === DELIVERY_APPOINTMENTS_STRING ? 'delivery' : 'terminal')"
            :selectionApi="{
              enabled: selection.enabled,
              keys: selection.keys,
              count: selection.count,
              allSelected: selection.allSelected,
              someSelected: selection.someSelected,
              toggleKey: selection.toggleKey,
              toggleAll: selection.toggleAll,
              isSelected: selection.isSelected,
              clearSelection: selection.clearSelection
            }"
            :isExportSelectionMode="isExportSelectionMode"
            :exportSelectionColumnMode="exportSelectionColumnMode"
            v-model:exportSelectionFormat="exportSelectionFormat"
            v-model:isExportSelectionModalOpen="isExportSelectionModalOpen"
          ></ContainerTable>
        </ListPage>
      </div>

      <div class="tw-mt-6 tw-flex tw-justify-center" v-if="totalPages > 1">
        <PaginationBar
          :page="pageNum"
          :totalPages="totalPages"
          :disabled="isActiveContainersLoading"
          @previousPage="prevPage"
          @nextPage="nextPage"
          @goToPage="goToPage"
        />
      </div>

    </div>

    <div v-if="!mainLoading && !user.onboardedTerminalLogins" class="tw-p-8 tw-text-center">
      <div class="tw-max-w-md tw-mx-auto tw-bg-white tw-rounded-2xl tw-p-6 tw-shadow-sm tw-border tw-border-slate-200">
        <h2 class="tw-text-xl tw-font-bold tw-text-slate-900 tw-mb-2">Welcome, {{ user.firstName }}!</h2>
        <p class="tw-text-slate-600 tw-mb-6">
          Please enter your terminal logins so we can fetch your containers for you.
        </p>
        <Profile @refreshContainers="refreshContainers"></Profile>
      </div>
    </div>

  </ListPage>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import HourGlass from '@carbon/icons-vue/es/hourglass/24';
import EventSchedule from '@carbon/icons-vue/es/event--schedule/24';
import { pushToast } from '@/lib/toast';

import ArchiveContainer from '@/gql/containers/ArchiveContainer.gql';
import UnarchiveContainer from '@/gql/containers/UnarchiveContainer.gql';
import ContainersQuery from '@/gql/containers/Containers.gql';
import ActiveContainerEventCountsQuery from '@/gql/containers/ActiveContainerEventCounts.gql';
import UserQuery from '@/gql/user/user.gql';

import Button from '@/components/core/Button.vue';
import SearchInput from '@/components/core/SearchInput.vue';
import TrackingHeader from '@/components/ContainerTracker/TrackingHeader.vue';
import PaginationBar from '@/components/core/PageElements/PaginationBar.vue';

import ArchivedContainersQuery from '../../gql/containers/ArchivedContainers.gql';
import ActiveContainersQuery from '../../gql/containers/ActiveContainers.gql';
import EnterContainer from './EnterContainer.vue';
import ContainerModal from './ContainerModal.vue';
import Modal from '@/components/core/Modal/Modal.vue';
import CopyButton from '@/components/core/CopyButton.vue';
import AddNotesModal from '@/components/core/AddNotesModal.vue';
import ContainerTable from './ContainerTable.vue';
import Profile from '../Profile/Profile.vue';
import Customers from '../../gql/customers/Customers.gql';
import ActiveBLs from '../../gql/orders/ActiveBLs.gql';
import YardStatusLabel from '../UI/YardStatusLabel.vue';
import AdvancedSort from '../core/AdvancedSort.vue';

import VesselsQuery from '../../gql/orders/Vessels.gql';
import { Container, K_SORT_BY } from '../../types';

import ListPage from '@/components/core/ListPage.vue';
import {
  getApolloClientFromVm,
  writeContainerEtaToCache,
  writeContainerNotesToCache,
} from '@/utils/containerApolloCache';
import { consumeTrackingContainersStale } from '@/utils/trackingContainersCache';
import {
  clearSavedListFilters,
  getIsListFiltersDifferentFromDefaults,
  loadSavedListFilters,
  saveListFilters,
} from '@/utils/savedListFilters';
import {
  loadSavedLastOpenedListItemId,
  saveLastOpenedListItemId,
} from '@/utils/savedLastOpenedListItem';
import { apolloClient } from '@/apollo';
import { useIdleDetection } from '@/composables/useIdleDetection';

type User = {
  activeContainers: [];
  archivedContainers: [];
  onboardedPayments: boolean;
  onboardedTerminalLogins: boolean;
  firstName: string;
};

const CONTAINER_SORT_FIELDS = [
  { label: 'Container Create Order', value: 'id' },
  { label: 'Container ##', value: 'container' },
  { label: 'B/L', value: 'bl' },
  { label: 'ETA/LFD', value: 'eta' },
  { label: 'Vessel Name', value: 'vessel' },
  { label: 'Yard Status', value: 'yard' },
  { label: 'Terminal', value: 'terminal' },
  { label: 'Customer', value: 'customer' },
  { label: 'Last Scrape', value: 'lastTerminalScrapeFetch' },
  { label: 'Scrape Update', value: 'lastTerminalScrapeUpdate' },
];

const TRACKING_LIST_FILTERS_PAGE_KEY = 'tracking';

const TRACKING_DEFAULT_LIST_FILTERS = {
  currentStatus: 'All',
  isShowingAppointments: false,
  searchTerm: '',
  sortBy: 'lastTerminalScrapeUpdate',
  sortOrder: 'DESC',
  multiSort: [
    { field: 'lastTerminalScrapeUpdate', order: 'DESC' },
  ],
  isAdvancedSort: false,
  pageSize: 50,
};

type ContainerEventCount = {
  id: string;
  containerNumber: string;
  containerEventCount: number;
};
// Helper: Wrap containers in object with index
function enumerateContainers(containers) {
  if (!containers) return [];
  return containers.map((cntnr, idx) => {
    return { container: cntnr, index: idx };
  });
}

export default defineComponent({
  name: 'ContainerTableView',
  setup() {
    // Pause the background poll while the user is away; the `isIdle` watcher
    // below resumes it. Detection auto-starts on mount and stops on unmount.
    const { isIdle, start: startIdleDetection, stop: stopIdleDetection } =
      useIdleDetection({ timeout: 60_000 });
    const listPageRef = ref(null);
    return { isIdle, startIdleDetection, stopIdleDetection, listPageRef };
  },
  components: {
    ListPage,
    EnterContainer,
    Modal,
    CopyButton,
    ContainerModal,
    AddNotesModal,
    ContainerTable,
    Profile,
    Button,
    HourGlass,
    EventSchedule,
    SearchInput,
    TrackingHeader,
    PaginationBar,
    AdvancedSort,
  },
  watch: {
    pageSize() {
      const vm = this as any;
      if (vm.pageNum !== 0) {
        vm.pageNum = 0;
      }
    },
    containers(list: any[]) {
      if (list?.length) {
        (this as any).shouldLoadVessels = true;
      }
    },

    // an old clunky way (for Vue3 options API) of wathing both arrays of new/updated containers and resetting the "show updated only" to false
    containerNumbersAddedSincePageLoad() {
      const vm = this as any;
      vm.onContainersAddedOrChangedSincePageLoad()
    },
    // an old clunky way (for Vue3 options API) of wathing both arrays of new/updated containers and resetting the "show updated only" to false
    containerNumbersChangedSincePageLoad() {
      const vm = this as any;
      vm.onContainersAddedOrChangedSincePageLoad()
    },
    // Turning NEW on clears the list filters so every new/changed container can be shown.
    // Values are set directly (not via changeStatus/searchTermChanged) so this reset does not turn NEW off again.
    isShowChangedAndNewContainersOnly(isShowing: boolean) {
      const vm = this as any;
      if (!isShowing) {
        return;
      }
      vm.isShowingAppointments = false;
      vm.currentStatus = 'All';
      vm.searchTerm = '';
      vm.searchInput = '';
      vm.pageNum = 0;
    },
    savedListFilters(filters) {
      if ((this as any).isListFiltered) {
        saveListFilters(TRACKING_LIST_FILTERS_PAGE_KEY, filters);
      } else {
        clearSavedListFilters(TRACKING_LIST_FILTERS_PAGE_KEY);
      }
    },
    // Pause the silent background refresh while the user is idle, and
    // refetch + resume the moment they come back.
    isIdle(idle: boolean) {
      const vm = this as any;
      if (idle) {
        vm.pauseBackgroundRefresh();
      } else {
        vm.resumeBackgroundRefresh();
      }
    },
  },
  computed: {
    // 1. Pagination & Stats
    totalPages(): number { return (this as any).pageCount || 1; },
    stats(): any {
        const meta = (this as any).listMeta?.stats;
        if (meta) {
          return meta;
        }
        return {
          all: 0,
          onboard: 0,
          available: 0,
          notAvailable: 0,
          offDock: 0,
          unknown: 0,
          notFound: 0,
          terminalAppointments: 0,
          deliveryAppointments: 0,
        };
    },

    // 2. Data for the Table
    // This feeds the SINGLE table in the template
    currentTabContainers(): any[] {
        return enumerateContainers((this as any).containers);
    },

    containers(): any[] {
      const list = (this as any).activeContainers;
      return Array.isArray(list) ? [...list] : [];
    },

    isOnboardedPayments(): any { return (this as any).user.onboardedPayments; },
    isSearching(): boolean { return (this as any).searchTerm.length > 0; },
    savedListFilters(): typeof TRACKING_DEFAULT_LIST_FILTERS {
      const vm = this as any;
      return {
        currentStatus: vm.currentStatus,
        isShowingAppointments: vm.isShowingAppointments,
        searchTerm: vm.searchTerm,
        sortBy: vm.sortBy,
        sortOrder: vm.sortOrder,
        multiSort: vm.multiSort,
        isAdvancedSort: vm.isAdvancedSort,
        pageSize: vm.pageSize,
      };
    },
    isListFiltered(): boolean {
      return getIsListFiltersDifferentFromDefaults(
        (this as any).savedListFilters,
        TRACKING_DEFAULT_LIST_FILTERS,
      );
    },
    isActiveContainersLoading(): boolean {
      const vm = this as any;
      const isQueryLoading = Boolean(vm.$apollo?.queries?.activeContainers?.loading);
      return isQueryLoading && !vm.isBackgroundActiveContainersRefreshing;
    },
  },
  data(): any {
    const savedListFilters = loadSavedListFilters(
      TRACKING_LIST_FILTERS_PAGE_KEY,
      TRACKING_DEFAULT_LIST_FILTERS,
    );
    return {
      DELIVERY_APPOINTMENTS_STRING: 'Delivery Appointments',
      TERMINAL_APPOINTMENTS_STRING: 'Terminal Appointments',
      currentContainerId: -1,
      lastOpenedContainerId: loadSavedLastOpenedListItemId(TRACKING_LIST_FILTERS_PAGE_KEY),
      // Row from list query — modal still loads extra fields (trips, etc.) via Container.gql
      currentContainerFromList: null,
      isContainerModalOpen: false,
      user: {} as User,
      listMeta: null as {
        total: number;
        pages: number;
        stats: Record<string, number>;
      } | null,
      // container numbers (ABCD1231223, NOT the internal ID) of active containers missing from the baseline
      containerNumbersAddedSincePageLoad: [] as string[],
      // container numbers (ABCD1231223, NOT the internal ID) whose terminal event count differs from the baseline
      containerNumbersChangedSincePageLoad: [] as string[],
      // container (load) ids of both lists above; sent as filterContainerIds while the NEW filter is on
      containerIdsAddedOrChangedSincePageLoad: [] as string[],
      // container id => event count, frozen at the first ActiveContainerEventCounts response after page load
      // (an entry is only updated when the container is opened, archived here, or reported as removed)
      containerEventCountBaselineById: null as Record<string, ContainerEventCount> | null,
      // container id => event count, from the latest ActiveContainerEventCounts response
      latestContainerEventCountById: {} as Record<string, ContainerEventCount>,
      activeContainerEventCountsRequestCount: 0,
      isRefreshingContainers: false,
      isUnmounting: false,
      searchInput: savedListFilters.searchTerm,
      searchTerm: savedListFilters.searchTerm,
      sortBy: savedListFilters.sortBy,
      sortOrder: savedListFilters.sortOrder,
      isShowingAppointments: savedListFilters.isShowingAppointments,
      mainLoading: true,
      multiSort: savedListFilters.multiSort,
      containerSortFields: CONTAINER_SORT_FIELDS,
      isAdvancedSort: savedListFilters.isAdvancedSort,

      isExportSelectionMode: false,
      exportSelectionFormat: 'html', // 'html' or 'csv' or text
      isExportSelectionModalOpen: false,
      exportSelectionColumnMode: 'multiple',

      // Pagination Variables
      pageNum: 0,
      pageSize: savedListFilters.pageSize,
      pageCount: 1,
      currentStatus: savedListFilters.currentStatus,

      activeBls: [],
      shouldLoadVessels: false,
      containerNotesModalOpen: false,
      containerForNotesModal: null as any,

      backgroundActiveContainersPollingInterval: null as any,
      isBackgroundActiveContainersRefreshing: false,

      isShowChangedAndNewContainersOnly: false,
    };
  },
  methods: {
    /** Vessel ETA lives on BL — update every row on this page with the same BL. */
    syncVesselEtaInList(billOfLadingId: number | string, eta: string) {
      const vm = this as any;
      const blId = Number(billOfLadingId);
      const list = vm.activeContainers;
      if (!Array.isArray(list)) {
        return;
      }
      const client = getApolloClientFromVm(vm);
      vm.activeContainers = list.map((c: any) => {
        if (c.billOfLading?.id != null && Number(c.billOfLading.id) === blId) {
          if (client && c.id != null) {
            writeContainerEtaToCache(client, c.id, eta);
          }
          return { ...c, eta };
        }
        return c;
      });
    },
    patchContainerInList(
      containerId: number | string,
      patch: Record<string, unknown>,
    ) {
      const vm = this as any;
      const list = vm.activeContainers;
      if (!Array.isArray(list)) {
        return;
      }
      const idx = list.findIndex(
        (c: any) => Number(c.id) === Number(containerId),
      );
      if (idx < 0) {
        return;
      }
      const next = list.slice();
      next[idx] = { ...next[idx], ...patch };
      vm.activeContainers = next;
    },
    /** Puts every saved filter back to its default (savedListFilters watcher clears storage) */
    resetListFilters() {
      const vm = this as any;
      const defaultListFilters = JSON.parse(JSON.stringify(TRACKING_DEFAULT_LIST_FILTERS));
      Object.assign(vm, defaultListFilters);
      vm.searchInput = defaultListFilters.searchTerm;
      vm.pageNum = 0;
      vm.isShowChangedAndNewContainersOnly = false;
    },
    // Switch Tabs -> Trigger Server Fetch
    changeStatus(newStatus: string) {
        const vm = (this as any);
        // picking a status pill (even the current one) leaves the NEW view
        vm.isShowChangedAndNewContainersOnly = false;
        if (vm.currentStatus === newStatus) return;
        vm.currentStatus = newStatus;
        if (vm.pageNum !== 0) {
            vm.pageNum = 0;
        }
    },

    // Pagination
    prevPage() {
      const vm = (this as any);
      if (vm.isActiveContainersLoading) return;
      if (vm.pageNum > 0) vm.pageNum -= 1;
    },
    nextPage() {
      const vm = (this as any);
      if (vm.isActiveContainersLoading) return;
      if (vm.pageNum + 1 < vm.pageCount) vm.pageNum += 1;
    },
    goToPage(idx: number) {
      const vm = (this as any);
      if (!vm.isActiveContainersLoading && idx !== vm.pageNum) vm.pageNum = idx;
    },

    // Search
    searchTermChanged(newSearchTerm: string) {
      const vm = (this as any);
      vm.isShowChangedAndNewContainersOnly = false;
      if (vm.searchTerm !== newSearchTerm) {
        vm.searchTerm = newSearchTerm
        if (vm.pageNum !== 0) {
          vm.pageNum = 0;
        }
      } else{
        if (vm.pageNum !== 0) {
          vm.pageNum = 0;
        }
      }
    },

    // Standard methods preserved
    toggleAppointmentsView() {
      const vm = (this as any);
      vm.isShowChangedAndNewContainersOnly = false;
      vm.isShowingAppointments = !vm.isShowingAppointments;

      if (vm.isShowingAppointments) {
          vm.changeStatus(vm.TERMINAL_APPOINTMENTS_STRING);
      } else {
          vm.changeStatus('All');
      }
    },
    showOrder(url){ (this as any).$router.push(url); },
    showNewOrder(url){ (this as any).$router.push(url); },

    updateSortBy(val) { 
        const vm = (this as any);

        if (vm.sortBy === val) {
          vm.sortOrder = vm.sortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
          vm.sortBy = val;
          vm.sortOrder = 'DESC';
        }

        vm.isAdvancedSort = false;
        vm.pageNum = 0;
    },
    handleAdvancedSort(newSort: any) {
      const vm = (this as any);
      vm.multiSort = newSort;
      vm.isAdvancedSort = true;
      vm.pageNum = 0;
    },
    reactivateAdvancedSort() {
      const vm = (this as any);
      if (vm.multiSort && vm.multiSort.length > 0) {
        vm.isAdvancedSort = true;
        vm.pageNum = 0;
      }
    },

    selectContainer(id) { (this as any).selectActiveContainer(id); },
    openContainerNotes(container: any) {
      const vm = this as any;
      vm.containerForNotesModal = container;
      vm.containerNotesModalOpen = true;
    },
    closeContainerNotes() {
      const vm = this as any;
      vm.containerNotesModalOpen = false;
      vm.containerForNotesModal = null;
    },
    onContainerNotesAdded(notes: any[]) {
      const vm = this as any;
      const container = vm.containerForNotesModal;
      if (container?.id == null) return;

      const notesList = Array.isArray(notes) ? notes : [];
      const client = getApolloClientFromVm(vm);
      if (client) {
        writeContainerNotesToCache(client, container.id, { notes: notesList });
      }
      vm.patchContainerInList(container.id, { notes: notesList });
      if (vm.containerForNotesModal) {
        vm.containerForNotesModal = { ...vm.containerForNotesModal, notes: notesList };
      }
    },
    onContainerArchivedNotesUpdated(archivedNotes: any[]) {
      const vm = this as any;
      const container = vm.containerForNotesModal;
      if (container?.id == null) return;

      const archivedNotesList = Array.isArray(archivedNotes) ? archivedNotes : [];
      const client = getApolloClientFromVm(vm);
      if (client) {
        writeContainerNotesToCache(client, container.id, {
          archivedNotes: archivedNotesList,
        });
      }
      vm.patchContainerInList(container.id, {
        archivedNotes: archivedNotesList,
      });
      if (vm.containerForNotesModal) {
        vm.containerForNotesModal = {
          ...vm.containerForNotesModal,
          archivedNotes: archivedNotesList,
        };
      }
    },
    onContainerUpdated(payload?: { billOfLadingId?: number | string; eta?: string }) {
      const vm = this as any;
      if (payload?.billOfLadingId != null && payload.eta !== undefined) {
        vm.syncVesselEtaInList(payload.billOfLadingId, payload.eta);
        return;
      }
      vm.refreshContainers();
    },
    async refreshContainers() {
      const vm = this as any;
      if (!vm.$apollo?.queries?.activeContainers) return;
      vm.isRefreshingContainers = true;
      try {
        await vm.$apollo.queries.activeContainers.refetch().then(() => {});
      } catch (error) {
        // keep going: the background refresh below must be re-armed even when a refetch fails
        console.error(error);
      } finally {
        vm.isRefreshingContainers = false;
      }
      // compared and re-armed here, not only in result(): Apollo skips result() when the refetched
      // page is identical, but containers on other pages / outside the filters may still have changed
      vm.fetchAndCompareActiveContainerEventCounts();
      vm.scheduleBackgroundRefresh();
      // void apolloClient.refetchQueries({ include: [ContainersQuery] });
    },
    /**
     * (Re)start the 30s countdown to the next silent background refresh. Not armed while the
     * user is idle (resumeBackgroundRefresh() refetches and re-arms), for users without
     * terminal logins, or once the page is unmounting.
     */
    scheduleBackgroundRefresh() {
      const vm = this as any;
      vm.pauseBackgroundRefresh();
      if (vm.isIdle || !vm.user?.onboardedTerminalLogins || vm.isUnmounting) {
        return;
      }
      vm.backgroundActiveContainersPollingInterval = setTimeout(async () => {
        // silent: hides the list loading overlay while the refetch runs
        vm.isBackgroundActiveContainersRefreshing = true;
        await vm.refreshContainers();
        vm.isBackgroundActiveContainersRefreshing = false;
      }, 30_000);
    },
    // Stop the pending background refresh; scheduleBackgroundRefresh() also skips re-arming
    // while isIdle is true, so polling stays paused until the user returns.
    pauseBackgroundRefresh() {
      const vm = this as any;
      clearTimeout(vm.backgroundActiveContainersPollingInterval);
      vm.backgroundActiveContainersPollingInterval = null;
    },
    // User is active again: silently refetch once; refreshContainers() re-arms the poll.
    async resumeBackgroundRefresh() {
      const vm = this as any;
      vm.isBackgroundActiveContainersRefreshing = true;
      await vm.refreshContainers();
      vm.isBackgroundActiveContainersRefreshing = false;
    },
    /**
     * Fetch every active container's terminal event count (unpaginated, ignores the list filters)
     * and compare it to the baseline. The first response after page load becomes the baseline.
     * Containers that disappeared since the baseline are reported once, in a toast the user closes.
     */
    async fetchAndCompareActiveContainerEventCounts() {
      const vm = this as any;
      vm.activeContainerEventCountsRequestCount += 1;
      const requestNumber = vm.activeContainerEventCountsRequestCount;

      let eventCounts: ContainerEventCount[] = [];
      try {
        const { data } = await vm.$apollo.query({
          query: ActiveContainerEventCountsQuery,
          fetchPolicy: 'network-only',
        });
        eventCounts = data?.activeContainerEventCounts || [];
      } catch (error) {
        console.error(error);
        return;
      }
      // a newer request was sent while this one was in flight; only the newest one is compared,
      // otherwise an older response could re-add a container already reported as removed
      if (requestNumber !== vm.activeContainerEventCountsRequestCount) {
        return;
      }

      const latestById: Record<string, ContainerEventCount> = {};
      for (const eventCount of eventCounts) {
        latestById[String(eventCount.id)] = eventCount;
      }
      vm.latestContainerEventCountById = latestById;

      if (!vm.containerEventCountBaselineById) {
        vm.containerEventCountBaselineById = { ...latestById };
        return;
      }

      const baselineById = vm.containerEventCountBaselineById;
      const removedContainerNumbers: string[] = [];
      for (const containerId of Object.keys(baselineById)) {
        if (!latestById[containerId]) {
          removedContainerNumbers.push(baselineById[containerId].containerNumber);
          // drop it from the baseline so the same removal is not reported again
          delete baselineById[containerId];
        }
      }
      if (removedContainerNumbers.length > 0) {
        pushToast(
          `${removedContainerNumbers.length} container(s) no longer in your active list (archived or deleted): ${removedContainerNumbers.join(', ')}`,
          'warning',
          0,
        );
      }

      vm.updateContainerBadgesFromBaseline();
    },
    /** "New" = not in the baseline; "changed" = event count differs from the baseline. */
    updateContainerBadgesFromBaseline() {
      const vm = this as any;
      const baselineById = vm.containerEventCountBaselineById || {};
      const addedContainerNumbers = new Set<string>();
      const changedContainerNumbers = new Set<string>();
      const addedOrChangedContainerIds: string[] = [];

      for (const [containerId, latest] of Object.entries(vm.latestContainerEventCountById) as [string, ContainerEventCount][]) {
        const baseline = baselineById[containerId];
        if (!baseline) {
          addedContainerNumbers.add(latest.containerNumber);
          addedOrChangedContainerIds.push(containerId);
        } else if (baseline.containerEventCount !== latest.containerEventCount) {
          changedContainerNumbers.add(latest.containerNumber);
          addedOrChangedContainerIds.push(containerId);
        }
      }

      vm.containerNumbersAddedSincePageLoad = Array.from(addedContainerNumbers);
      vm.containerNumbersChangedSincePageLoad = Array.from(changedContainerNumbers);
      vm.containerIdsAddedOrChangedSincePageLoad = addedOrChangedContainerIds;
    },
    /** Opening a container clears its badges: its baseline becomes its latest event count. */
    clearContainerBadgesByUpdatingBaseline(containerId: number | string) {
      const vm = this as any;
      const latest = vm.latestContainerEventCountById[String(containerId)];
      if (!latest || !vm.containerEventCountBaselineById) {
        return;
      }
      vm.containerEventCountBaselineById[String(containerId)] = { ...latest };
      vm.updateContainerBadgesFromBaseline();
    },
    // an old clunky way (for Vue3 options API) of wathing both arrays of new/updated containers and resetting the "show updated only" to false
    onContainersAddedOrChangedSincePageLoad() {
      const vm = this as any;
      if (vm.containerNumbersAddedSincePageLoad.length + vm.containerNumbersChangedSincePageLoad.length === 0) {
        vm.isShowChangedAndNewContainersOnly = false;
      }
    },

    // UI Helpers
    selectActiveContainer(containerId: number) {
      const vm = (this as any);
      vm.currentContainerId = containerId;
      vm.lastOpenedContainerId = String(containerId);
      saveLastOpenedListItemId(TRACKING_LIST_FILTERS_PAGE_KEY, containerId);
      vm.currentContainerFromList = vm.containers.find(
        (container: any) => Number(container.id) === Number(containerId),
      );
      vm.clearContainerBadgesByUpdatingBaseline(containerId);
      vm.isContainerModalOpen = true;
    },
    closeContainerModal() {
      const vm = (this as any);
      try {
        const path = (vm.$route && vm.$route.path)
          || (vm.$router && vm.$router.currentRoute && vm.$router.currentRoute.path)
          || '';
        if (typeof path === 'string' && path.includes('/tracking') && path !== '/tracking/') {
          vm.$router.replace('/tracking/');
        }
      } catch (e) {
        // defensive: router shape unexpected, ignore
      }
      vm.isContainerModalOpen = false;
      vm.currentContainerId = -1;
    },
    archiveContainer(containerId: number) {
      const vm = (this as any);
      const container = vm.containers.find(
        (c: any) => Number(c.id) === Number(containerId),
      );
      if (!container) return;
      vm.$apollo.mutate({
          mutation: ArchiveContainer,
          variables: { containerId: container.id },
      }).then(() => {
          pushToast(`Archived Container ${container.containerId}`, 'success', 5000);
          // archived here, by this user: drop it from the baseline so it is not reported as removed
          if (vm.containerEventCountBaselineById) {
            delete vm.containerEventCountBaselineById[String(container.id)];
          }
          vm.refreshContainers();
      });
    },
  },
  beforeUnmount() {
    const vm = (this as any);
    // a refresh still in flight must not re-arm the poll after unmount
    vm.isUnmounting = true;
    vm.pauseBackgroundRefresh()
  },
  apollo: {
    activeContainers: {
      query: ContainersQuery,
      debounce: 200,
      variables() {
        const vm = (this as any);

        const finalSort = vm.isAdvancedSort
          ? vm.multiSort
          : [{ field: vm.sortBy, order: vm.sortOrder }];

        return {
          pageNum: (this as any).pageNum,
          pageSize: (this as any).pageSize,
          searchQuery: (this as any).searchTerm,
          status: (this as any).currentStatus,
          multiSort: finalSort,
          // NEW filter: only the new/changed containers, across all pages (null = no id filter)
          filterContainerIds: vm.isShowChangedAndNewContainersOnly
            ? vm.containerIdsAddedOrChangedSincePageLoad
            : null,
        };
      },
      update(data) {
          // IMPORTANT: Return just the array to satisfy any mixins or watchers expecting an array
          // The metadata is handled in result()
          if(data.activeContainers && data.activeContainers.containers) {
              return data.activeContainers.containers;
          }
          return [];
      },
      result({ data }) {
        // console.log('activeContainers result', new Date())
        const vm = (this as any);
        vm.mainLoading = false;

        if (consumeTrackingContainersStale()) {
          setTimeout(() => vm.refreshContainers(), 0);
          return;
        }

        if (data?.activeContainers) {
          vm.listMeta = {
            total: data.activeContainers.total,
            pages: data.activeContainers.pages,
            stats: data.activeContainers.stats,
          };
          vm.pageCount = data.activeContainers.pages;
        }

        // // first load and filter/search/sort/page changes; refreshContainers() compares after its own refetch
        // if (!vm.isRefreshingContainers) {
        //   vm.fetchAndCompareActiveContainerEventCounts();
        // }

        // // a fetch from anywhere (e.g. a search filter or status change) restarts the 30s countdown
        // vm.scheduleBackgroundRefresh();

        if (vm.$route.params.containerId && vm.currentContainerId === -1) {
          vm.$nextTick(() => {
            vm.selectActiveContainer(Number(vm.$route.params.containerId));
          });
        }
      },
      error(error) {
         (this as any).mainLoading = false;
         console.error(error);
      }
    },
    user: {
      query: UserQuery,
      result({ data }) {
        const vm = (this as any);
        if (data && data.user) vm.user = data.user;
        vm.mainLoading = false;
        // the list may have loaded before the user (no terminal logins known yet => poll not armed)
        if (!vm.backgroundActiveContainersPollingInterval) {
          vm.scheduleBackgroundRefresh();
        }
      }
    },
    vessels: {
        query: VesselsQuery,
        skip() {
          const vm = (this as any);
          return vm.shouldLoadVessels;
        }
    },
  },

});
</script>

<style scoped lang="scss">
.tracking-page {
  min-height: calc(100vh - 60px);
}

.tracking-tabs {
  flex-wrap: wrap;
}

.tracking-tabs .uk-button {
  margin-top: 4px;
}

.rel {
  position: relative;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 5;
}
</style>